import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useParams } from "react-router-dom";
import api from "../utils/axiosConfig";
import { io } from "socket.io-client";

const RealtimeChat = () => {
  const { user } = useAuth();
  const { friendId } = useParams();
  const [friend, setFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState(null);
  const socket = useRef();

  useEffect(() => {
    const fetchFriend = async () => {
      try {
        setChatLoading(true);
        setError(null);
        const res = await api.get(`/api/users/${friendId}`);
        setFriend(res.data);
      } catch (error) {
        console.error("Lỗi khi lấy thông tin người bạn:", error);
        setError(error.response?.data?.message || "Không tìm thấy thông tin người bạn hoặc lỗi server.");
      } finally {
        setChatLoading(false);
      }
    };

    const fetchMessages = async () => {
      try {
        setChatLoading(true);
        setError(null);
        const res = await api.get(`/api/users/messages/${friendId}`);
        setMessages(res.data || []);
      } catch (error) {
        console.error("Lỗi khi lấy tin nhắn:", error);
        setError(error.response?.data?.message || "Không thể tải tin nhắn. Vui lòng kiểm tra lại.");
      } finally {
        setChatLoading(false);
      }
    };

    if (friendId) {
      fetchFriend();
      fetchMessages();
    }
  }, [friendId]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Không tìm thấy token. Vui lòng đăng nhập lại.");
      return;
    }

    socket.current = io("http://localhost:5000", {
      withCredentials: true,
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

    socket.current.on("connect", () => {
      console.log("Kết nối WebSocket thành công:", socket.current.id);
      if (user) socket.current.emit("joinRoom", user._id);
    });

    socket.current.on("receiveMessage", (message) => {
      // Kiểm tra trùng lặp dựa trên message._id
      setMessages((prev) => {
        if (!prev.some((msg) => msg._id === message._id)) {
          return [...prev, message];
        }
        return prev;
      });
    });

    socket.current.on("messageRead", (data) => {
      setMessages((prev) =>
        prev.map((msg) => (msg._id === data.messageId ? { ...msg, status: "read" } : msg))
      );
    });

    socket.current.on("messagesRead", (data) => {
      if (data.receiverId === user._id) {
        setMessages((prev) =>
          prev.map((msg) => (msg.senderId === data.receiverId ? { ...msg, status: "read" } : msg))
        );
      }
    });

    socket.current.on("error", (errorData) => {
      console.error("Lỗi từ WebSocket:", errorData.message);
      setError(errorData.message || "Lỗi kết nối WebSocket.");
    });

    socket.current.on("connect_error", (err) => {
      console.error("Lỗi kết nối WebSocket:", err.message);
      setError("Không thể kết nối đến WebSocket server. Vui lòng kiểm tra lại.");
    });

    return () => {
      socket.current.disconnect();
    };
  }, [user]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !friend) return;

    try {
      setChatLoading(true);
      setError(null);
      const messageData = {
        senderId: user._id,
        receiverId: friend._id,
        content: newMessage,
      };
      socket.current.emit("sendMessage", messageData);
      setNewMessage(""); // Xóa nội dung input sau khi gửi
    } catch (error) {
      console.error("Lỗi khi gửi tin nhắn:", error);
      setError("Lỗi khi gửi tin nhắn: " + (error.response?.data?.message || error.message));
    } finally {
      setChatLoading(false);
    }
  };

  const markMessageAsRead = (messageId) => {
    socket.current.emit("markMessageAsRead", messageId);
  };

  useEffect(() => {
    messages.forEach((msg) => {
      if (msg.receiverId === user._id && msg.status !== "read") {
        markMessageAsRead(msg._id);
      }
    });
  }, [messages, user]);

  if (!user) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg border border-gray-200 mt-10">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
        Chat với {friend ? friend.name : "Người dùng"}
      </h2>

      {error && <p className="text-center text-red-500 mb-4">{error}</p>}

      <div className="border rounded-lg p-4 shadow">
        <div className="h-64 overflow-y-auto mb-4 border rounded p-2 bg-gray-50">
          {chatLoading ? (
            <p className="text-center text-gray-500">Đang tải tin nhắn...</p>
          ) : messages.length > 0 ? (
            messages.map((msg) => (
              <div
                key={msg._id || Math.random()}
                className={`p-2 mb-2 rounded ${
                  msg.senderId === user._id ? "bg-blue-100 ml-auto" : "bg-gray-200"
                } max-w-xs`}
                onClick={() => msg.receiverId === user._id && msg.status !== "read" && markMessageAsRead(msg._id)}
              >
                <p>{msg.content}</p>
                <p className="text-xs text-gray-500">
                  {new Date(msg.createdAt).toLocaleTimeString()}
                  {msg.senderId === user._id && (
                    <span className="ml-2">
                      {msg.status === "sent" && "Đã gửi"}
                      {msg.status === "delivered" && "Đã nhận"}
                      {msg.status === "read" && "Đã đọc"}
                    </span>
                  )}
                </p>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">Chưa có tin nhắn.</p>
          )}
        </div>

        <form onSubmit={sendMessage} className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Nhập tin nhắn..."
            className="flex-1 border p-2 rounded shadow-sm"
            disabled={chatLoading}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
            disabled={chatLoading || !newMessage.trim()}
          >
            Gửi
          </button>
        </form>
      </div>
    </div>
  );
};

export default RealtimeChat;